import { apiClient } from './client';
import type { ApiResponse } from '@/types/api.types';

// Mirrors myLedger PostingRules DTOs (config-driven posting engine, P4).
export interface PostingRuleListDto {
  id: string;
  ruleCode: string;
  ruleName: string;
  sourceEventType: string;
  targetDocumentType: string;
  ruleType: string;
  priority: number;
  governanceStatus: string;
  versionNo: number;
  isActive: boolean;
  lineCount: number;
  conditionCount: number;
}

export interface PostingRuleConditionDto {
  attribute: string;
  operator: string;
  value?: string | null;
  conditionExpression?: string | null;
  groupNo?: number | null;
}

export interface PostingRuleLineDto {
  lineNumber: number;
  drCr: string;
  amountBasis: string;
  includeWhen?: string | null;
  iterator?: string | null;
  accountSource: string;
  accountRole: string;
  accountRef?: string | null;
  partySource: string;
  descriptionTemplate?: string | null;
}

export interface PostingRuleDetailDto {
  id: string;
  ruleCode: string;
  ruleName: string;
  description?: string | null;
  sourceEventType: string;
  targetDocumentType: string;
  ruleType: string;
  priority: number;
  stopOnMatch: boolean;
  aggregationPolicy: string;
  fxGainLossMode: string;
  governanceStatus: string;
  versionNo: number;
  isActive: boolean;
  conditions: PostingRuleConditionDto[];
  lines: PostingRuleLineDto[];
  /** First business date the rule applies to (yyyy-MM-dd). */
  effectiveFrom: string;
}

export interface PostingPreviewLine {
  drCr: string;
  accountCode: string;
  accountName: string;
  amount: number;
  narration?: string | null;
}

export interface PostingPreviewResult {
  matched: boolean;
  ruleId?: string | null;
  ruleCode?: string | null;
  targetDocumentType?: string | null;
  balanced: boolean;
  totalDebit: number;
  totalCredit: number;
  lines: PostingPreviewLine[];
  error?: string | null;
}

export interface SetupPackDto {
  id: string;
  packType: string;
  packCode: string;
  packName: string;
  description?: string | null;
  versionNo: number;
  itemCount: number;
  isSystem: boolean;
}

export interface PackInstallItemDto {
  ruleCode: string;
  description: string;
  action: 'CREATE' | 'UPDATE' | 'SKIP';
}

export interface PackInstallResultDto {
  items: PackInstallItemDto[];
  warnings: string[];
}

export const postingRuleApi = {
  async list(): Promise<PostingRuleListDto[]> {
    const r = await apiClient.get('/config/posting-rules');
    return r.data?.data ?? [];
  },
  async get(id: string): Promise<PostingRuleDetailDto | null> {
    const r = await apiClient.get(`/config/posting-rules/${id}`);
    return r.data?.data ?? null;
  },
  /** Dry-run: shows the double-entry a sample event would produce, without posting. */
  async preview(event: unknown): Promise<PostingPreviewResult> {
    const r = await apiClient.post('/config/posting-rules/preview', event);
    return r.data?.data;
  },
  async create(req: SavePostingRuleRequest): Promise<{ id: string }> {
    const r = await apiClient.post('/config/posting-rules', req);
    return r.data?.data;
  },
  async update(id: string, req: SavePostingRuleRequest): Promise<{ id: string }> {
    const r = await apiClient.put(`/config/posting-rules/${id}`, req);
    return r.data?.data;
  },

  // ── Governance lifecycle: Draft → Submitted → Approved → Active (+ Rejected / Suspended) ──
  submit:   (id: string) => apiClient.post(`/config/posting-rules/${id}/submit`),
  approve:  (id: string) => apiClient.post(`/config/posting-rules/${id}/approve`),
  reject:   (id: string, reason: string) => apiClient.post(`/config/posting-rules/${id}/reject`, { reason }),
  activate: (id: string) => apiClient.post(`/config/posting-rules/${id}/activate`),
  suspend:  (id: string) => apiClient.post(`/config/posting-rules/${id}/suspend`),

  // ── Setup packs: reuse rules between companies without hand-authoring JSON ───
  /** This company's rules, as a pack file — download it and import it into another company. */
  exportPack: () => apiClient.get('/config/posting-rules/export', { responseType: 'blob' }),
  listPacks: async (): Promise<SetupPackDto[]> => {
    const r = await apiClient.get('/config/posting-rules/packs');
    return r.data?.data ?? [];
  },
  importPack: (req: { packCode: string; packName: string; description?: string | null; definition: string }) =>
    apiClient.post('/config/posting-rules/packs/import', req),
  installPack: async (code: string, dryRun: boolean): Promise<PackInstallResultDto> => {
    const r = await apiClient.post(`/config/posting-rules/packs/${code}/install?dryRun=${dryRun}`);
    return r.data?.data;
  },

  // ── Rule Library: shipped rules, switched on/off per company (404631) ──────
  library: async (): Promise<RuleLibraryAreaDto[]> => {
    const r = await apiClient.get<ApiResponse<RuleLibraryAreaDto[]>>('/config/posting-rules/library');
    return r.data?.data ?? [];
  },
  enableLibraryRule:  (code: string) => apiClient.post(`/config/posting-rules/library/${code}/enable`),
  disableLibraryRule: (code: string) => apiClient.post(`/config/posting-rules/library/${code}/disable`),
  /** Bring an enabled rule up to the version this release ships (status UPDATE_AVAILABLE). */
  updateLibraryRule:  (code: string) => apiClient.post(`/config/posting-rules/library/${code}/update`),
};

export type RuleLibraryStatus = 'NOT_ENABLED' | 'ENABLED' | 'UPDATE_AVAILABLE' | 'DISABLED' | 'EDITED' | 'CODE_IN_USE';

export interface RuleLibraryItemDto {
  ruleCode: string;
  ruleName: string;
  sourceEventType: string;
  targetDocumentType: string;
  summary: string;
  status: RuleLibraryStatus;
  /** Account roles the rule posts to, and whether Posting Accounts maps each yet. */
  accounts: { role: string; mapped: boolean }[];
  usesPaymentModes: boolean;
  /** Other live rules for the same event. */
  otherActiveRules: string[];
}

export interface RuleLibraryAreaDto {
  area: string;
  source: string;
  rules: RuleLibraryItemDto[];
}

export interface SaveConditionDto {
  attribute: string;
  operator: string;
  value?: string | null;
  conditionExpression?: string | null;
  groupNo?: number | null;
}

export interface SaveLineDto {
  lineNumber: number;
  drCr: string;
  amountBasis: string;
  includeWhen?: string | null;
  iterator?: string | null;
  accountSource: string;
  accountRole: string;
  accountRef?: string | null;
  partySource: string;
  descriptionTemplate?: string | null;
}

export interface SavePostingRuleRequest {
  ruleCode: string;
  ruleName: string;
  description?: string | null;
  sourceEventType: string;
  targetDocumentType: string;
  ruleType: string;
  priority: number;
  stopOnMatch: boolean;
  aggregationPolicy: string;
  fxGainLossMode: string;
  governanceStatus: string;
  isActive: boolean;
  conditions: SaveConditionDto[];
  lines: SaveLineDto[];
  /** yyyy-MM-dd; empty = today for a new rule, unchanged for an edit. */
  effectiveFrom?: string | null;
}

export const ACCOUNT_SOURCES = [
  'FIXED', 'ACCOUNT_ROLE_MAP', 'PARTY_CONTROL', 'PAYMENT_MODE_MAP', 'TAX_CODE_MAP',
  'PRODUCT_CATEGORY', 'PRODUCT', 'COMPANY_DEFAULT', 'VOUCHER_GROUP_MAP', 'CARD_ACQUIRER_MAP',
];
export const TARGET_DOCS = ['GL_JOURNAL', 'AP_BILL', 'AP_DEBIT_NOTE', 'AR_INVOICE', 'AR_CREDIT_NOTE'];
export const ITERATORS = ['', 'tenders', 'lines', 'taxes', 'revenueByCategory', 'components', 'vouchers', 'voucherRedemptions'];
